import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ICarrier } from 'src/app/models/ICarrier.model';
import { IWorker } from 'src/app/models/IWorker.model';
import { Orders } from 'src/app/models/Orders.model';
import { Priority } from 'src/app/models/Priority.model';
import { Products } from 'src/app/models/Product.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { ProductsService } from 'src/app/services/products.service';
import { DropdownService } from 'src/app/services/shared/dropdown.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * OrdiniParzialiDettagliComponent — modale di assegnazione di un
 * ordine parziale (lanciata da OrdiniParzialiListComponent, Chunk E.1).
 *
 * Lascia all'admin:
 *   • leggere i dati ordine (cliente + indirizzo + note)
 *   • assegnare operatore, priorità e corriere
 *   • selezionare i prodotti da prelevare (quantità ≤ ordinata)
 *   • inviare il payload `IOrderToSend` al BE
 *
 * Sub-modale: `app-product-detail` (apre la posizione magazzino di un
 * prodotto cliccato).
 *
 * Phase 1.5 / v2.8.0 — TS cleaned up (Chunk E.2):
 *   • dropped kendo-dialog/kendo-formfield/kendo-combobox/
 *     kendo-dropdownlist/kendoTextBox (79 kendo refs → 0 in HTML),
 *   • dropped 27 console.log/debug,
 *   • dropped `addOrderDialog` (~50 LOC dead, ricostruiva form mai
 *     usato con regex email su carrier_id),
 *   • dropped `clearForm`, `goBackFunction`, `close`, `open`,
 *     `modify` (handler dead),
 *   • dropped 4 state morti: `dataSaved`, `addOrder`, `assignOrder`,
 *     `selectedProds`, `chosenProd`,
 *   • dropped dialog dimensions hardcoded (`minWidth=1400`,
 *     `dialogWidth=600`, `dialogHeight=1017`, `maxlength=250`),
 *   • fixed `carrier_id` → `or_carrier_id` (formControlName allineato
 *     con `Orders.IOrderToSend.or_carrier_id` e con la validation
 *     check che era silent-fail nel codice legacy),
 *   • 5 subscribe annidate in ngOnInit → forkJoin singolo
 *     (`takeUntilDestroyed`) — memory leak fix,
 *   • `changeProductNumber` rifatto come `buildNumbers(n)` puro
 *     (era invocato ad ogni click sulla dropdown),
 *   • switch to inject() + ChangeDetectorRef.detectChanges() su
 *     state mutation in callback,
 *   • `selectedNumbers` calcolato 1 volta per prodotto, non ad ogni
 *     click (perf su ordini con >50 prodotti).
 *
 * Behaviour preserved 1:1:
 *   • payload `addOrderMethod` (incluso `or_agent_id:'1'` e
 *     `or_customer_reference:'123'` — convenzione BE per assegnazione
 *     manuale via admin),
 *   • mutazione `op_qta := op_qta_picked` prima del send (BE
 *     conserva la qta originale altrove),
 *   • `or_partial` recomputed dopo ogni pick (S = parziale, N = pieno).
 */
@Component({
  selector: 'app-ordini-parziali-dettagli',
  templateUrl: './ordini-parziali-dettagli.component.html',
  styleUrls: ['./ordini-parziali-dettagli.component.scss'],
  standalone: false,
})


export class OrdiniParzialiDettagliComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly ordersServ = inject(OrdersService);
  private readonly productServ = inject(ProductsService);
  private readonly dropdownServ = inject(DropdownService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs ────────────────────────────────────────────────────────
  @Input() isAssegnaOn = false;
  @Input() orderId = -1;
  @Output() result = new EventEmitter<boolean>();

  // ── Form ──────────────────────────────────────────────────────────
  public assignOrderForm!: FormGroup;

  // ── Data ──────────────────────────────────────────────────────────
  public orderNew!: Orders.IOrderNew;
  public productsNew: Products.IProductToSend[] = [];
  public priorityOptions: Priority.IPriority[] = [];
  public carriers: ICarrier[] = [];
  public workers: IWorker[] = [];

  // ── UI state ──────────────────────────────────────────────────────
  public loading = false;
  public sending = false;
  public allSelected = false;
  /** S = ordine resta parziale (qualcosa non prelevato) · N = chiuso */
  public partialOrder: 'S' | 'N' | null = null;
  public formattedDate = '—';
  /** Map: op_code → array di quantità selezionabili (1..op_qta).
   *  Pre-computed per evitare il rebuild ad ogni click dropdown. */
  public qtyOptions: Record<string, number[]> = {};
  /** Map: op_code → product_id, usato per aprire product-detail. */
  private codeToProductId: Record<string, number> = {};
  public clickedProductId: number | null = null;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.assignOrderForm = this.formBuilder.group({
      or_order_number: [''],
      or_order_date: [''],
      or_order_priority_cod: ['', Validators.required],
      or_carrier_id: [''],
      or_customer: [''],
      or_shipping_address: [''],
      or_shipping_zip: [''],
      or_shipping_city: [''],
      or_note: [''],
      or_shipping_prov: [''],
      or_shipping_country: [''],
      or_assigned_id: ['', Validators.required],
    });

    this.loadOrder();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loading — order + dropdowns in parallel.
  // ──────────────────────────────────────────────────────────────────

  private loadOrder(): void {
    this.loading = true;

    forkJoin({
      order: this.ordersServ.getNewOrder(String(this.orderId)),
      priorities: this.dropdownServ.getPriorities(),
      carriers: this.dropdownServ.getCarriers(),
      workers: this.dropdownServ.getWorkers(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ order, priorities, carriers, workers }) => {
          this.orderNew = new Orders.OrderNew((order as IWsResult).data);
          this.productsNew = this.buildPickingProducts(this.orderNew);
          this.qtyOptions = this.buildAllQtyOptions(this.productsNew);

          this.priorityOptions = priorities;
          this.carriers = carriers;
          this.workers = (workers ?? []).map((w) => ({
            ...w,
            full_name: `${w.ac_first_name} ${w.ac_last_name}`,
          }));

          if (this.orderNew.or_order_date) {
            this.formattedDate = formatDate(
              this.orderNew.or_order_date,
              'dd/MM/yyyy',
              'it',
            );
          }

          this.assignOrderForm.patchValue({
            or_order_number: this.orderNew.or_order_number,
            or_order_date: this.formattedDate,
            or_carrier_id: this.orderNew.or_carrier_id ?? '',
            or_customer: this.orderNew.or_customer,
            or_shipping_address: this.orderNew.or_shipping_address,
            or_shipping_zip: this.orderNew.or_shipping_zip,
            or_shipping_city: this.orderNew.or_shipping_city,
            or_shipping_prov: this.orderNew.or_shipping_prov,
            or_shipping_country: this.orderNew.or_shipping_country,
            or_note: this.orderNew.or_note,
          });

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: 'Impossibile caricare i dati dell\'ordine.',
            timer: 1800,
            timerProgressBar: true,
          });
        },
      });
  }

  /** Maps the IOrderNew.products[] (server shape) to the picking
   *  shape used in this modal: starts with op_checked=false and
   *  op_qta_picked = op_qta (full quantity). */
  private buildPickingProducts(
    order: Orders.IOrderNew,
  ): Products.IProductToSend[] {
    return (order.products ?? []).map((item) => ({
      op_note: item.or_note,
      op_checked: false,
      op_code: item.op_code,
      op_description: item.op_description,
      op_qta: item.op_qta,
      op_weigth: item.op_weigth,
      op_stock: item.op_stock,
      op_qta_picked: +item.op_qta,
      op_qta_ordered: item.op_qta_ordered,
      op_positions: [],
      tab_order_status_cod: [],
    }));
  }

  /** Pre-computes 1..op_qta arrays for each product code so the
   *  template doesn't rebuild them at every dropdown click. */
  private buildAllQtyOptions(
    products: Products.IProductToSend[],
  ): Record<string, number[]> {
    const map: Record<string, number[]> = {};
    for (const p of products) {
      const n = Number(p.op_qta) || 0;
      map[p.op_code] = Array.from({ length: n }, (_, i) => i + 1);
    }
    return map;
  }

  // ──────────────────────────────────────────────────────────────────
  // Picking actions.
  // ──────────────────────────────────────────────────────────────────

  togglePicked(index: number): void {
    const p = this.productsNew[index];
    p.op_checked = !p.op_checked;
    this.recomputePartialFlag();
    this.recomputeAllSelected();
  }

  selectAllProducts(): void {
    this.allSelected = !this.allSelected;
    this.productsNew.forEach((item) => (item.op_checked = this.allSelected));
    this.recomputePartialFlag();
  }

  /** Called when the user picks a new quantity from the dropdown.
   *  Auto-marks the product as picked and clamps below op_qta. */
  updateQuantity(picked: number, index: number): void {
    const p = this.productsNew[index];
    const max = Number(p.op_qta) || 0;
    p.op_qta_picked = Math.min(Math.max(0, Number(picked) || 0), max);
    p.op_checked = true;
    this.recomputePartialFlag();
    this.recomputeAllSelected();
  }

  private recomputePartialFlag(): void {
    const allChecked = this.productsNew.every((v) => v.op_checked);
    const allFull = this.productsNew.every(
      (v) => +v.op_qta === v.op_qta_picked,
    );
    this.partialOrder = allChecked && allFull ? 'N' : 'S';
  }

  private recomputeAllSelected(): void {
    this.allSelected =
      this.productsNew.length > 0 &&
      this.productsNew.every((p) => p.op_checked);
  }

  // ──────────────────────────────────────────────────────────────────
  // Product detail sub-modal.
  // ──────────────────────────────────────────────────────────────────

  productDetails(product: Products.IProductToSend): void {
    this.productServ
      .getProduct(product.op_code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((productData: IWsResult) => {
        // op_code -> product_id mapping cached for the sub-modal
        const id = productData?.data?.[0]?.product_id;
        if (id != null) this.codeToProductId[product.op_code] = id;
        this.clickedProductId = id ?? null;
        this.closeServ.openProd();
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Submit.
  // ──────────────────────────────────────────────────────────────────

  get isReady(): boolean {
    return (
      this.assignOrderForm.valid &&
      !this.sending &&
      this.productsNew.length > 0 &&
      this.productsNew.some((p) => p.op_checked)
    );
  }

  submit(): void {
    if (!this.isReady) return;
    this.sending = true;

    const f = this.assignOrderForm.value;
    const assignOrder: Orders.IOrderToSend = {
      or_assignment_date: this.orderNew.or_order_date,
      or_assigned_id: f.or_assigned_id,
      or_order_priority_cod: f.or_order_priority_cod,
      or_carrier_id: f.or_carrier_id,
      or_partial: this.partialOrder ?? 'S',
      // NB: hardcoded by BE convention for manual admin assignments
      or_customer_reference: '123',
      or_agent_id: '1',
      // BE expects op_qta == op_qta_picked at send time
      products: this.productsNew.map((item) => ({
        ...item,
        op_qta: item.op_qta_picked,
      })),
      or_order_number: f.or_order_number,
      or_order_date: this.orderNew.or_order_date,
      or_customer: f.or_customer,
      or_shipping_address: f.or_shipping_address,
      or_shipping_city: f.or_shipping_city,
      or_shipping_prov: f.or_shipping_prov,
      or_shipping_country: f.or_shipping_country,
      or_gamma_id: this.orderNew.or_gamma_id,
      or_customer_code: this.orderNew.or_customer_code,
      or_customer_mail: this.orderNew.or_customer_mail,
      or_customer_phone: this.orderNew.or_customer_phone,
      or_shipping_zip: f.or_shipping_zip,
      or_quote_number: this.orderNew.or_quote_number,
      or_quote_date: this.orderNew.or_quote_date,
      or_subject: this.orderNew.or_subject,
      or_note: this.orderNew.or_note,
      or_boxes: this.orderNew.or_boxes,
      or_shipping_customer: this.orderNew.or_shipping_customer,
      or_carrier_desc: this.orderNew.or_carrier_desc,
    };

    this.ordersServ
      .addOrder(assignOrder)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ordersData) => {
          this.sending = false;
          if (ordersData?.success) {
            this.goBack();
            this.result.emit(true);
            Swal.fire({
              icon: 'success',
              title: 'Assegnato!',
              text: 'Ordine assegnato con successo!',
              timer: 1500,
              timerProgressBar: true,
            });
          } 
          else {
            Swal.fire({
              icon: 'error',
              title: 'Non assegnato',
              text: 'Si è verificato un problema, riprova.',
              timer: 1500,
              timerProgressBar: true,
            });
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.sending = false;
          this.cdr.detectChanges();
          Swal.fire({
            icon: 'error',
            title: 'Non assegnato',
            text: 'Si è verificato un problema, riprova.',
            timer: 1500,
            timerProgressBar: true,
          });
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  goBack(): void {
    this.closeServ.closeAssegnaDet();
  }
  
}
